import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useLocation } from 'react-router-dom'
import { selectAuth } from './redux/authSlice'
import { setNotification } from './redux/notificationSlice'
import { fetchUserInfo, resetTimeoutCount, selectUser } from './redux/userSlice'
import { fetchModules, selectModules } from './redux/moduleSlice'
import { fetchDropdowns, selectDropdowns } from './redux/dropdownSlice'
import { fetchPortfolios, selectPortfolios } from "./redux/portfolioSlice"
import { isBefore, parseISO, parseJSON, addHours, isPast } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import axios from "axios"
import { useMsal, useIsAuthenticated, AuthenticatedTemplate } from "@azure/msal-react"
import { checkMsalAuth } from "./msalConfig"
import { InteractionStatus } from "@azure/msal-browser"
import { useRequest } from "./hooks/UseRequest"
import UserPageViewAudit from "./components/UserPageViewAudit";
import { NotificationService } from "./services/NotificationService";

/**
 * This wrapper component handles checking if the user is logged into Azure AD
 *  it will also grab the base information from the db that we want to have cached.
 * @returns {JSX.Element}
 * @constructor
 */
export default function RequiredAuth() {
  const { instance, accounts, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const [refresh, setRefresh] = useState('')
  const dispatch = useDispatch()
  const auth = useSelector(selectAuth)
  const user = useSelector(selectUser)
  const modules = useSelector(selectModules)
  const dropdowns = useSelector(selectDropdowns)
  const portfolios = useSelector(selectPortfolios)
  const location = useLocation()
  const requests = useRequest()
  const appTriedMessage = 'There was an error loading the data from backend. Max retries exceeded.'
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  const { markNotificationAsViewed } = NotificationService()

  // Log out this tab when another tab clears persist:root (i.e. user logged out there)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'persist:root' && e.newValue === null) {
        instance.logoutRedirect()
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [instance])

  /**
   * Will run every 10 minutes and refresh auth silently
   */
  useEffect(() => {
    // 10 minutes
    const timeout = 600000
    const idleInterval = setInterval(() => {
      if (inProgress === InteractionStatus.None) {
        checkMsalAuth(dispatch, instance, accounts, auth, location, false)
      }
    }, timeout)

    return () => clearInterval(idleInterval)
  }, [])

  /**
   * Will run on every page transition and reload to check whether the user is logged in and attempt to get a new
   * token if needed.
   */
  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      checkMsalAuth(dispatch, instance, accounts, auth, location, true)
      dispatch(resetTimeoutCount())
    }
  }, [isAuthenticated, inProgress, location.key])

  /**
   * Will run on every page transition and mark a notification as viewed if required
   */
  useEffect(() => {
    if (location.state?.notification_id) {
      markNotificationAsViewed(location.state.notification_id);
    }
  }, [location.key])

  /**
   * Will run on every page transition to check if we need to reload any of the localStorage cached Redux items.
   * NOTE: Due to an issue with MSAL returning a different token on every call we DO NOT need to include location.key
   *  in the dependency array. If we are able to fix the MSAL issue then we would need to add location.key to ensure
   *  that this runs on every page.
   */
  useEffect(() => {
    if (!auth.fromPageChange) { return }
    const app_refresh = () => {
      return axios.get(
        `${process.env.REACT_APP_API_DOMAIN}/app`,
        {
          headers: {
            Authorization: `Bearer ${auth.accessToken}`,
          },
        }
      );
    }
    if (auth.accessToken) {
      app_refresh().then((res) => setRefresh(res.data.data))
    }
  }, [auth.accessToken])

  useEffect(() => {
    if (!auth.fromPageChange) { return }
    const api_cache = utcToZonedTime(parseJSON(refresh.user_cache_needs_refresh), tz)
    const local_cache = parseISO(user.last_refresh)

    if (user.api_tries > 4) {
      dispatch(setNotification({ type: 'error', body: appTriedMessage, buttons: ['retryCache'] }))
    }

    if (user && auth.accessToken && (['failed', 'idle'].includes(user.status) || isBefore(local_cache, api_cache) || isPast(addHours(local_cache, 12)))) {
      dispatch(fetchUserInfo(auth.accessToken))
    }
  }, [user, auth.accessToken, refresh])

  useEffect(() => {
    if (!auth.fromPageChange) { return }
    const api_cache = utcToZonedTime(parseJSON(refresh.module_cache_needs_refresh), tz)
    const local_cache = parseISO(modules.last_refresh)

    if (modules.api_tries > 4) {
      dispatch(setNotification({ type: 'error', body: appTriedMessage, buttons: ['retryCache'] }))
    }

    if (user && auth.accessToken && (['failed', 'idle'].includes(modules.status) || isBefore(local_cache, api_cache) || isPast(addHours(local_cache, 12)))) {
      dispatch(fetchModules(auth.accessToken))
    }
  }, [user, modules, auth.accessToken, refresh])

  useEffect(() => {
    if (!auth.fromPageChange) { return }
    const api_cache = utcToZonedTime(parseJSON(refresh.dropdowns_cache_needs_refresh), tz)
    const local_cache = parseISO(dropdowns.last_refresh)

    if (dropdowns.api_tries > 4) {
      dispatch(setNotification({ type: 'error', body: appTriedMessage, buttons: ['retryCache'] }))
    }

    if (user && auth.accessToken && (['failed', 'idle'].includes(dropdowns.status) || isBefore(local_cache, api_cache) || isPast(addHours(local_cache, 12)))) {
      dispatch(fetchDropdowns(auth.accessToken))
    }
  }, [user, dropdowns, auth.accessToken, refresh])

  useEffect(() => {
    if (!auth.fromPageChange) { return }
    const api_cache = utcToZonedTime(parseJSON(refresh.portfolios_cache_needs_refresh), tz)
    const local_cache = parseISO(portfolios.last_refresh)

    if (portfolios.api_tries > 4) {
      dispatch(setNotification({ type: 'error', body: appTriedMessage, buttons: ['retryCache'] }))
    }

    if (user && auth.accessToken && (['failed', 'idle'].includes(portfolios.status) || isBefore(local_cache, api_cache) || isPast(addHours(local_cache, 12)))) {
      dispatch(fetchPortfolios(auth.accessToken))
    }
  }, [user, portfolios, auth.accessToken, refresh])

  return (
    <AuthenticatedTemplate>
      <UserPageViewAudit />
      <Outlet />
    </AuthenticatedTemplate>
  )
}
